import { toMoney } from '@sequencehq/utils'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CREDIT_BILL_LABEL,
  CREDIT_TAG_LABEL,
  getGrantPrice,
  isDiscountedGrant,
  REFRESH_LABELS
} from 'CreditGrants/index'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { CreditGrant, Phase } from 'modules/Cube/domain/cube.domain.types'

export type CreditGrantRowModel = {
  id: string
  name: string
  typeLabel: string
  // What the customer is invoiced, so the column reads like every other row.
  price: string
  // The face value, set only when it differs from the price.
  grantedCredit: string
  repeats: string
  bill: string
  // Product names the grant applies to (always the full list, never "all").
  appliedTo: string[]
  onView: () => void
  onDelete: () => void
}

export const useCreditGrantRows = ({
  phaseId
}: {
  phaseId: Phase['id']
}): {
  rows: CreditGrantRowModel[]
  grantToDelete: CreditGrant | null
  clearGrantToDelete: () => void
  removeGrant: (grantId: string) => void
} => {
  const navigate = useNavigate()
  const cubeContext = useCubeContext()
  const [grantToDelete, setGrantToDelete] = useState<CreditGrant | null>(null)

  const currency =
    cubeContext.queries.selectedCurrency ??
    cubeContext.queries.rawData.configuration.currency.default
  const resolvedPhase = cubeContext.queries.resolvedPhases[phaseId]
  // Grants are already dropped at load when the feature is off; keep the view
  // inert too so nothing staged elsewhere can leak into a flag-off editor.
  const creditGrantsEditingEnabled =
    cubeContext.queries.rawData.configuration.features.creditGrantsEditing
  const grants = creditGrantsEditingEnabled
    ? (resolvedPhase?.creditGrants ?? [])
    : []
  const prices = resolvedPhase?.prices ?? []

  const rows = useMemo<CreditGrantRowModel[]>(() => {
    const priceNamesById = new Map(prices.map(price => [price.id, price.name]))

    return grants.map(grant => {
      const grantCurrency = grant.currency ?? currency

      return {
        id: grant.id,
        name: grant.name,
        typeLabel: CREDIT_TAG_LABEL,
        price: toMoney({
          value: String(getGrantPrice(grant)),
          currency: grantCurrency
        }),
        grantedCredit: isDiscountedGrant(grant)
          ? `${toMoney({
              value: String(grant.amount),
              currency: grantCurrency
            })} credit granted`
          : '',
        repeats:
          grant.grantRefreshFrequency === 'NONE'
            ? ''
            : REFRESH_LABELS[grant.grantRefreshFrequency],
        bill: CREDIT_BILL_LABEL,
        // Always list the products the grant applies to; empty priceIds means
        // every price in the phase.
        appliedTo: grant.priceIds.length
          ? grant.priceIds.flatMap(priceId => {
              const name = priceNamesById.get(priceId)
              return name ? [name] : []
            })
          : prices.map(price => price.name),
        onView: () => navigate(`./credit-editor/${phaseId}/${grant.id}`),
        onDelete: () => setGrantToDelete(grant)
      }
    })
  }, [grants, prices, currency, navigate, phaseId])

  const removeGrant = useCallback(
    (grantId: string) => {
      const phase = cubeContext.queries.rawData.data.phases[phaseId]
      if (!phase) {
        return
      }
      cubeContext.mutators.updateData({
        phases: {
          [phaseId]: {
            creditGrantIds: phase.creditGrantIds.filter(id => id !== grantId)
          }
        }
      })
    },
    [cubeContext, phaseId]
  )

  const clearGrantToDelete = useCallback(() => setGrantToDelete(null), [])

  return { rows, grantToDelete, clearGrantToDelete, removeGrant }
}
